// Approval chains (D3, D4, D9, D11; rulings Q2, Q5, Q8) — the composer's
// per-item chain panel for an item in a DRAFT package. Shows the template's
// current default beside THIS send's chain, which staff may adjust: reorder,
// remove a non-mandatory stage, add a D2 role, pick co-borrowers / guarantors
// on the Buyer stage. Mandatory stages are locked. Captions and statements are
// READ-ONLY here (rulings D2/Q5/Q12 — wording is template-level, ADMIN-only):
// the server stores and prints only the template's (or Appendix C's) wording
// for each role, and an added role shows that wording (templateWordingFor). Also: the content mode
// (merge the template body, or upload a final PDF / DOCX), the two D9
// attestations on a borrower-facing template, and the "Who will receive this"
// preflight. The host's Send stays disabled until every panel reports ready.
//
// PATCH semantics on PUT …/chain: each save sends ONLY the member the user
// changed; everything else is null and carries forward server-side.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload, X } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
  ResponsiveGrid,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  approvalChainsApi,
  type ApprovalChainDefinition,
  type ApprovalChainDto,
  type ApprovalChainRole,
  type ApprovalChainStageDefinition,
  type BuyerAccountOption,
  type ItemApprovalChainRequest,
} from "@/api/approvalChains";
import {
  approvalChainKeys,
  invalidateApprovalChainReads,
} from "@/hooks/collection/approvalChainKeys";
import { useDateFormatter } from "@/utils/datetime";
import { APPROVAL_CHAIN_STATUS_TONE } from "@/components/Collection/collectionStatusLabels";
import { CounselPlaceholderChip, MandatoryLock } from "./ApprovalChainEditor";
import {
  canMoveStage,
  canRemoveStage,
  chainSignature,
  isLockedAt,
  moveStage,
  roleLabel,
  stageHasPlaceholder,
  templateWordingFor,
  unusedRoles,
  validateChain,
} from "./approvalChainRules";

/** D9 — the two staff attestations, verbatim (owner-approved wording). */
export const ENGAGEMENT_ATTESTATION_WORDING =
  "I confirm the Broker Engagement Acknowledgment for this transaction has been signed by the Licensed Intermediary, the Seller and Phoenix.";
export const CLEARANCE_ATTESTATION_WORDING =
  "I confirm an Intermediary Clearance Determination issued by Phoenix's internal counsel is in effect for this transaction.";

export interface ChainReadiness {
  ready: boolean;
  /** Why Send is held, in the reader's words. Empty when ready. */
  reasons: string[];
}

export interface ApprovalChainComposerPanelProps {
  packageId: string;
  itemId: string;
  itemName: string;
  /** The package's deal (owner), for the loan-guarantor hint. */
  dealId: string | null;
  /** The host's permission check; editing needs Collection.ManagePackages
   *  (the server re-checks every save). */
  hasPermission: (permission: string) => boolean;
  /** Reports whether this item lets the package send. */
  onReadinessChange?: (itemId: string, readiness: ChainReadiness) => void;
}

/** The chain this send uses when staff have not adjusted it. */
export function effectiveChain(chain: ApprovalChainDto): ApprovalChainDefinition {
  return chain.definition ?? chain.templateDefault ?? { stages: [] };
}

/** Readiness from the server's chain + preflight and the panel's local state. */
export function chainReadiness(input: {
  chain: ApprovalChainDto | null | undefined;
  preflightProblems: string[] | undefined;
  dirty: boolean;
  validationErrors: string[];
}): ChainReadiness {
  const { chain, preflightProblems, dirty, validationErrors } = input;
  if (chain === null) return { ready: true, reasons: [] };
  if (chain === undefined) return { ready: false, reasons: ["Loading the approval chain…"] };
  const reasons: string[] = [];
  if (dirty) reasons.push("Save the routing changes before sending.");
  reasons.push(...validationErrors);
  if (chain.isBorrowerFacing && (!chain.engagementAttestation || !chain.clearanceAttestation))
    reasons.push("Confirm both attestations before sending.");
  if (chain.contentMode === "Upload" && !chain.uploadFileName)
    reasons.push("Upload the final document, or switch back to merging the template.");
  if (preflightProblems === undefined) reasons.push("Checking who will receive this…");
  else reasons.push(...preflightProblems);
  return { ready: reasons.length === 0, reasons };
}

export function ApprovalChainComposerPanel({
  packageId,
  itemId,
  itemName,
  dealId,
  hasPermission,
  onReadinessChange,
}: ApprovalChainComposerPanelProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const canManage = hasPermission("Collection.ManagePackages");

  const chainQuery = useQuery({
    queryKey: approvalChainKeys.item(itemId),
    queryFn: () => approvalChainsApi.getForItem(itemId),
  });
  const chain = chainQuery.data;

  const preflightQuery = useQuery({
    queryKey: approvalChainKeys.preflight(packageId, itemId),
    queryFn: () => approvalChainsApi.preflight(packageId, itemId),
    enabled: Boolean(chain),
  });

  // Local edits to THIS send's chain; null = untouched (shows the server's).
  const [draft, setDraft] = useState<ApprovalChainDefinition | null>(null);
  const [roleToAdd, setRoleToAdd] = useState<ApprovalChainRole | "">("");
  const [accountSearch, setAccountSearch] = useState("");
  const [knownAccounts, setKnownAccounts] = useState<Record<string, BuyerAccountOption>>({});
  const [contentModeChoice, setContentModeChoice] = useState<"Merge" | "Upload" | null>(null);

  const baseline = chain?.templateDefault ?? null;
  const serverChain = chain ? effectiveChain(chain) : null;
  const current = draft ?? serverChain;
  const stages = current?.stages ?? [];
  const dirty = draft !== null && chainSignature(draft) !== chainSignature(serverChain);
  const validationErrors = useMemo(
    () => (chain && draft ? validateChain(draft, chain.isBorrowerFacing, baseline) : []),
    [chain, draft, baseline],
  );
  const hasBuyerStage = stages.some((s) => s.role === "Buyer");

  const guarantorsQuery = useQuery({
    queryKey: approvalChainKeys.loanGuarantors(dealId ?? ""),
    queryFn: () => approvalChainsApi.loanGuarantors(dealId!),
    enabled: Boolean(chain && dealId && hasBuyerStage),
  });
  const searchTerm = accountSearch.trim();
  const accountsQuery = useQuery({
    queryKey: approvalChainKeys.buyerAccounts(searchTerm),
    queryFn: () => approvalChainsApi.buyerAccounts(searchTerm),
    enabled: canManage && searchTerm.length >= 2,
  });

  const saveMut = useMutation({
    mutationFn: (body: ItemApprovalChainRequest) =>
      approvalChainsApi.save(packageId, itemId, body),
    onSuccess: (dto, body) => {
      qc.setQueryData(approvalChainKeys.item(itemId), dto);
      if (body.definition || body.useTemplateDefault) setDraft(null);
      if (body.contentMode) setContentModeChoice(null);
      invalidateApprovalChainReads(qc);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => approvalChainsApi.upload(packageId, itemId, file),
    onSuccess: (dto) => {
      qc.setQueryData(approvalChainKeys.item(itemId), dto);
      setContentModeChoice(null);
      invalidateApprovalChainReads(qc);
      toast.success(`Uploaded ${dto.uploadFileName ?? "the document"}.`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const readiness = chainReadiness({
    chain,
    preflightProblems: preflightQuery.data?.problems,
    dirty,
    validationErrors,
  });
  const readinessKey = JSON.stringify(readiness);
  useEffect(() => {
    onReadinessChange?.(itemId, JSON.parse(readinessKey) as ChainReadiness);
  }, [itemId, readinessKey, onReadinessChange]);

  if (chainQuery.isLoading) {
    return (
      <p className="text-caption text-gray-mid m-0">Loading the approval chain for {itemName}…</p>
    );
  }
  if (chainQuery.isError) {
    return (
      <p className="text-small text-status-error m-0" role="alert">
        Could not load the approval chain for {itemName}: {extractErrorMessage(chainQuery.error)}
      </p>
    );
  }
  if (!chain || !current) return null;

  const edit = (next: ApprovalChainStageDefinition[]) => setDraft({ stages: next });
  const patchStage = (index: number, patch: Partial<ApprovalChainStageDefinition>) =>
    edit(stages.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const addable = unusedRoles(stages);
  const busy = saveMut.isPending || uploadMut.isPending;

  const addRole = () => {
    if (!roleToAdd) return;
    // The wording the server will store: the template's for this role, else
    // Appendix C's. Never typed here.
    edit([
      ...stages,
      { role: roleToAdd, mandatory: false, ...templateWordingFor(roleToAdd, baseline, chain.isBorrowerFacing) },
    ]);
    setRoleToAdd("");
  };

  const coSignerIds = (stage: ApprovalChainStageDefinition) => stage.coSignerPortalUserIds ?? [];
  const addCoSigner = (index: number, account: BuyerAccountOption) => {
    const stage = stages[index];
    if (coSignerIds(stage).includes(account.portalUserId)) return;
    setKnownAccounts((prev) => ({ ...prev, [account.portalUserId]: account }));
    patchStage(index, { coSignerPortalUserIds: [...coSignerIds(stage), account.portalUserId] });
  };
  const removeCoSigner = (index: number, id: string) => {
    const remaining = coSignerIds(stages[index]).filter((x) => x !== id);
    patchStage(index, { coSignerPortalUserIds: remaining.length > 0 ? remaining : null });
  };

  const contentMode = contentModeChoice ?? chain.contentMode;
  const guarantors = guarantorsQuery.data ?? [];
  const preflight = preflightQuery.data;

  return (
    <section
      className="flex flex-col gap-space-4 rounded-md border border-border-light bg-white-pure p-space-4"
      data-testid={`approval-chain-panel-${itemId}`}
      aria-label={`Approval chain — ${itemName}`}
    >
      <header className="flex flex-wrap items-center gap-space-2">
        <h3 className="text-body font-semibold text-onyx m-0">Approval chain — {itemName}</h3>
        <Badge status={APPROVAL_CHAIN_STATUS_TONE[chain.status] ?? "draft"}>{chain.statusLabel}</Badge>
        {chain.isBorrowerFacing && <Badge status="info">Borrower-facing</Badge>}
      </header>

      <ResponsiveGrid cols={2} minColWidth="18rem" gap="sm">
        <div className="flex flex-col gap-space-2" data-testid="chain-template-default">
          <h4 className="text-small font-semibold text-onyx m-0">Template default</h4>
          {(baseline?.stages ?? []).length === 0 ? (
            <p className="text-caption text-gray-mid m-0">The template carries no default chain.</p>
          ) : (
            <ol className="m-0 pl-space-5 flex flex-col gap-space-1 text-small text-onyx">
              {baseline!.stages.map((s, i) => (
                <li key={`${i}-${s.role}`}>
                  {roleLabel(s.role)}
                  <span className="text-caption text-gray-mid">
                    {" "}
                    · {s.mandatory ? "Mandatory" : "Optional"} · {s.caption}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex flex-col gap-space-2" data-testid="chain-this-send">
          <h4 className="text-small font-semibold text-onyx m-0">This send</h4>
          <p className="text-caption text-gray-mid m-0" data-testid="chain-wording-note">
            Signature-page wording comes from the template and can’t be changed per send.
          </p>
          <ol className="m-0 p-0 list-none flex flex-col gap-space-2">
            {stages.map((stage, index) => {
              const locked = isLockedAt(baseline, index);
              return (
                <li
                  key={`${index}-${stage.role}`}
                  className="flex flex-col gap-space-2 rounded-md border border-border-light p-space-3"
                  data-testid={`send-stage-${index}`}
                >
                  <div className="flex flex-wrap items-center gap-space-2">
                    <span className="text-small font-semibold text-onyx">
                      {index + 1}. {roleLabel(stage.role)}
                    </span>
                    {locked && <MandatoryLock />}
                    {!locked && stage.mandatory && (
                      <span className="text-caption text-gray-mid">Mandatory</span>
                    )}
                    {stageHasPlaceholder(stage) && <CounselPlaceholderChip />}
                    <span className="flex-1" />
                    {canManage && !locked && (
                      <>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Move ${roleLabel(stage.role)} up`}
                          disabled={busy || !canMoveStage(stages, index, -1, baseline)}
                          onClick={() => edit(moveStage(stages, index, -1))}
                        >
                          <ArrowUp size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Move ${roleLabel(stage.role)} down`}
                          disabled={busy || !canMoveStage(stages, index, 1, baseline)}
                          onClick={() => edit(moveStage(stages, index, 1))}
                        >
                          <ArrowDown size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Remove ${roleLabel(stage.role)}`}
                          disabled={busy || !canRemoveStage(stages, index, baseline)}
                          onClick={() => edit(stages.filter((_, i) => i !== index))}
                        >
                          <Trash2 size={14} aria-hidden />
                        </Button>
                      </>
                    )}
                  </div>

                  <p className="text-caption text-gray-mid m-0" data-testid={`send-stage-wording-${index}`}>
                    {stage.caption} — {stage.statement}
                  </p>

                  {stage.role === "Buyer" && (
                    <div className="flex flex-col gap-space-2" data-testid="co-signer-picker">
                      <span className="text-caption font-medium text-onyx">
                        Co-borrowers and guarantors (sign at this stage)
                      </span>
                      {coSignerIds(stage).length === 0 ? (
                        <span className="text-caption text-gray-mid">None added.</span>
                      ) : (
                        <ul className="m-0 p-0 list-none flex flex-wrap gap-space-2">
                          {coSignerIds(stage).map((id) => (
                            <li
                              key={id}
                              className="inline-flex items-center gap-space-1 rounded-sm border border-border-light px-space-2 text-caption text-onyx"
                            >
                              {knownAccounts[id]
                                ? `${knownAccounts[id].fullName} · ${knownAccounts[id].email}`
                                : "Selected Buyer portal account"}
                              {canManage && (
                                <button
                                  type="button"
                                  className="inline-flex items-center text-gray-mid hover:text-onyx"
                                  aria-label={`Remove ${knownAccounts[id]?.fullName ?? "co-signer"}`}
                                  onClick={() => removeCoSigner(index, id)}
                                >
                                  <X size={12} aria-hidden />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {coSignerIds(stage).length > 0 && (
                        <p className="text-caption text-gray-mid m-0">
                          {stage.coSignerCaption} — {stage.coSignerStatement}
                        </p>
                      )}
                      {canManage && (
                        <>
                          <FormField label="Find a Buyer portal account">
                            <Input
                              value={accountSearch}
                              placeholder="Name or email"
                              onChange={(e) => setAccountSearch(e.target.value)}
                              autoComplete="off"
                            />
                          </FormField>
                          {searchTerm.length >= 2 && (
                            <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                              {accountsQuery.isLoading ? (
                                <li className="text-caption text-gray-mid">Searching…</li>
                              ) : (accountsQuery.data ?? []).length === 0 ? (
                                <li className="text-caption text-gray-mid">
                                  No Buyer portal account matches “{searchTerm}”.
                                </li>
                              ) : (
                                (accountsQuery.data ?? []).map((a) => (
                                  <li key={a.portalUserId} className="flex items-center gap-space-2">
                                    <span className="text-small text-onyx">
                                      {a.fullName}
                                      <span className="text-caption text-gray-mid"> · {a.email}</span>
                                    </span>
                                    <Button
                                      type="button"
                                      variant="tertiary"
                                      size="sm"
                                      aria-label={`Add ${a.fullName}`}
                                      disabled={coSignerIds(stage).includes(a.portalUserId)}
                                      onClick={() => addCoSigner(index, a)}
                                    >
                                      Add
                                    </Button>
                                  </li>
                                ))
                              )}
                            </ul>
                          )}
                        </>
                      )}
                      {guarantors.length > 0 && (
                        <div className="flex flex-col gap-space-1" data-testid="loan-guarantor-hint">
                          <span className="text-caption font-medium text-onyx">
                            Guarantors on the loan
                          </span>
                          <span className="text-caption text-gray-mid">
                            For reference only — they are not added automatically. Add each one
                            who should sign by finding their Buyer portal account above.
                          </span>
                          <ul className="m-0 pl-space-5 text-caption text-onyx">
                            {guarantors.map((g) => (
                              <li key={`${g.email}-${g.name}`}>
                                {g.name} · {g.email}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          {canManage && (
            <div className="flex flex-wrap items-end gap-space-2">
              <FormField label="Add a stage">
                <Select
                  value={roleToAdd}
                  onChange={(e) => setRoleToAdd(e.target.value as ApprovalChainRole | "")}
                  disabled={addable.length === 0 || busy}
                >
                  <option value="">
                    {addable.length === 0 ? "Every role is in the chain" : "Choose a role…"}
                  </option>
                  {addable.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <Button type="button" variant="secondary" onClick={addRole} disabled={!roleToAdd || busy}>
                <Plus size={14} aria-hidden /> Add stage
              </Button>
            </div>
          )}
        </div>
      </ResponsiveGrid>

      {validationErrors.length > 0 && (
        <ul
          className="m-0 rounded-md bg-status-error/10 p-space-3 pl-space-6 text-small text-onyx"
          data-testid="composer-chain-errors"
        >
          {validationErrors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}

      {canManage && (dirty || chain.definition !== null) && (
        <div className="flex flex-wrap items-center gap-space-2">
          {dirty && (
            <Button
              type="button"
              onClick={() => saveMut.mutate({ definition: draft! })}
              disabled={busy || validationErrors.length > 0}
              loading={saveMut.isPending && Boolean(saveMut.variables?.definition)}
            >
              Save routing changes
            </Button>
          )}
          {dirty && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => setDraft(null)}
              disabled={busy}
            >
              Discard changes
            </Button>
          )}
          {!dirty && chain.definition !== null && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => saveMut.mutate({ useTemplateDefault: true })}
              disabled={busy}
              loading={saveMut.isPending && Boolean(saveMut.variables?.useTemplateDefault)}
            >
              Use the template default
            </Button>
          )}
        </div>
      )}

      <fieldset className="m-0 flex flex-col gap-space-2 rounded-md border border-border-light p-space-3">
        <legend className="px-space-2 text-caption font-medium text-gray-mid">Document content</legend>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="radio"
            name={`content-mode-${itemId}`}
            checked={contentMode === "Merge"}
            disabled={!canManage || busy}
            onChange={() =>
              chain.contentMode === "Merge"
                ? setContentModeChoice(null)
                : saveMut.mutate({ contentMode: "Merge" })
            }
          />
          Merge — render the template's letter body with this deal's details
        </label>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="radio"
            name={`content-mode-${itemId}`}
            checked={contentMode === "Upload"}
            disabled={!canManage || busy}
            onChange={() => setContentModeChoice("Upload")}
          />
          Upload — send a final PDF or Word document instead
        </label>
        {contentMode === "Upload" && (
          <div className="flex flex-col gap-space-1 pl-space-5">
            {chain.uploadFileName && (
              <span className="text-caption text-onyx" data-testid="chain-upload-file">
                Uploaded: {chain.uploadFileName}
              </span>
            )}
            {canManage && (
              <label className="inline-flex items-center gap-space-2 text-caption text-onyx">
                <Upload size={14} aria-hidden />
                {chain.uploadFileName ? "Replace the file" : "Choose a file"} (PDF or DOCX, up to 25 MB)
                <input
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  disabled={busy}
                  aria-label="Upload the final document"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadMut.mutate(file);
                    e.target.value = "";
                  }}
                  className="text-caption"
                />
              </label>
            )}
          </div>
        )}
      </fieldset>

      {chain.isBorrowerFacing && (
        <fieldset
          className="m-0 flex flex-col gap-space-2 rounded-md border border-border-light p-space-3"
          data-testid="chain-attestations"
        >
          <legend className="px-space-2 text-caption font-medium text-gray-mid">
            Required before sending
          </legend>
          {(
            [
              {
                key: "engagementAttested" as const,
                wording: ENGAGEMENT_ATTESTATION_WORDING,
                attestation: chain.engagementAttestation,
              },
              {
                key: "clearanceAttested" as const,
                wording: CLEARANCE_ATTESTATION_WORDING,
                attestation: chain.clearanceAttestation,
              },
            ]
          ).map(({ key, wording, attestation }) => (
            <label key={key} className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-space-1"
                required
                checked={attestation !== null}
                disabled={!canManage || busy}
                onChange={(e) => saveMut.mutate({ [key]: e.target.checked })}
              />
              <span className="flex flex-col">
                <span>{wording}</span>
                {attestation && (
                  <span className="text-caption text-gray-mid">
                    Confirmed by {attestation.userName ?? "a staff user"} · {dateTime(attestation.at)}
                  </span>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="flex flex-col gap-space-2" data-testid="chain-preflight">
        <h4 className="text-small font-semibold text-onyx m-0">Who will receive this</h4>
        {preflightQuery.isLoading ? (
          <p className="text-caption text-gray-mid m-0">Checking each party…</p>
        ) : preflightQuery.isError ? (
          <p className="text-small text-status-error m-0" role="alert">
            Could not check the parties: {extractErrorMessage(preflightQuery.error)}
          </p>
        ) : preflight ? (
          <>
            <ol className="m-0 p-0 list-none flex flex-col gap-space-1">
              {preflight.parties.map((p) => (
                <li
                  key={`${p.order}-${p.role}-${p.partyEmail ?? p.partyName ?? ""}`}
                  className="flex flex-wrap items-baseline gap-space-2 text-small"
                >
                  <span className="font-medium text-onyx">
                    {p.order}. {p.roleLabel}
                  </span>
                  {p.resolved ? (
                    <span className="text-onyx">
                      {p.partyName}
                      {p.partyEmail ? <span className="text-gray-mid"> · {p.partyEmail}</span> : null}
                    </span>
                  ) : (
                    <span className="text-status-error">{p.problem ?? "Not resolved."}</span>
                  )}
                </li>
              ))}
            </ol>
            {preflight.problems.length > 0 && (
              <ul
                className="m-0 rounded-md bg-status-error/10 p-space-3 pl-space-6 text-small text-onyx"
                data-testid="chain-preflight-problems"
              >
                {preflight.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
